"use client"
import { useCallback } from 'react'
import { type PlanId } from '@/lib/branchTypes'
import { usePerspectiveSelection } from '@/app/common/blocks/perspective-choice/usePerspectiveSelection'
import {
  type PlanATimelineDiagramProps,
  type TimelineEndpointEntry,
} from './planATimelineDiagramModel'
import {
  runDiagramEndpointNavigation,
  runDiagramNavigation,
  type DiagramNavigation,
} from './timelineDiagramNavigation'
import { useSelectedEnding } from './useSelectedEnding'

// Jump to a plan and optionally a section inside it — the primitive every plan
// and branchpoint label calls. Shared by the desktop labels and mobile chips.
export type DiagramNavigate = (plan: PlanId, opts?: Omit<DiagramNavigation, 'plan'>) => void

// Shared navigation wiring for both the desktop and mobile timeline diagrams.
// Turns the diagram's props into a `navigate` primitive + an endpoint handler,
// and surfaces the active perspective so labels can highlight the current POV.
export const useTimelineDiagramNavigation = ({
  currentPlan,
  selectPlan,
  setEpilogueExpanded,
}: PlanATimelineDiagramProps) => {
  const { selected, setPerspective } = usePerspectiveSelection()
  // Which ending (Epilogue / Public / Insider) is currently expanded below the
  // diagram, or null for the main scenario. Drives the bold/large highlight that
  // moves from Plan A onto the open ending, and the end-cap's reviewer-box hide.
  const selectedEnding = useSelectedEnding()

  // Plan + branchpoint jumps. They always head somewhere that isn't the
  // epilogue, so they collapse it on the way (epilogueExpanded defaults false).
  const navigate: DiagramNavigate = useCallback(
    (plan, opts) =>
      runDiagramNavigation({ plan, setEpilogueExpanded, ...opts }, currentPlan, selectPlan),
    [currentPlan, selectPlan, setEpilogueExpanded],
  )

  const handleEndpoint = useCallback(
    (entry: TimelineEndpointEntry) =>
      runDiagramEndpointNavigation(entry, { setEpilogueExpanded, setPerspective }),
    [setEpilogueExpanded, setPerspective],
  )

  return { navigate, handleEndpoint, selected, selectedEnding, currentPlan }
}
